'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { PublicRouteGuard, useAuth } from '@/auth';
import GoogleSignInButton from '@/auth/components/GoogleSignInButton';
import { AmazonSignInButton } from '@/auth/components/AmazonSignInButton';
import { TelegramSignInButton } from '@/auth/components/TelegramSignInButton';
import { validateEmail } from '@/utils/validators';
import { stashTrialBlurFromUrl } from '@/store/hooks/useTrialBlur';
import { Button, Input, PasswordInput, Spinner } from '@UIKit';
import styles from './login.module.css';
import logoicon from '@Assets/logo.svg';
import loginBg from '@Assets/bg/login_bg.jpg';
import shieldIcon from '@Assets/icons/shield-login.svg';
import lockIcon from '@Assets/icons/lock-login.svg';
import zipIcon from '@Assets/icons/zip-login.svg';

type LoginView = 'default' | 'magic-link-sent' | 'password' | 'forgot-password' | 'forgot-password-sent';

const RESEND_COOLDOWN = 60;

const LoginPage: React.FC = () => {
    const t = useTranslations('login');
    const {
        isLoading,
        error,
        clearError,
        sendMagicLink,
        loginWithPassword,
        requestPasswordReset,
    } = useAuth();

    const [view, setView] = useState<LoginView>('default');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const [isWebView, setIsWebView] = useState(false);

    // WE-446: pre-auth capture of `?trial_blur=1` so it survives the
    // login round-trip. The drained value is written to users/storage by
    // `useTrialBlurUrlParam()` after auth.
    useEffect(() => {
        stashTrialBlurFromUrl(new URLSearchParams(window.location.search));
    }, []);

    useEffect(() => {
        const ua = navigator.userAgent;
        const win = window as unknown as Record<string, unknown>;
        const isTelegramWebView = !!(
            win.TelegramWebviewProxy
            || win.TelegramWebviewProxyProto
            || /Telegram/i.test(ua)
        );
        if (isTelegramWebView
            || /\bwv\b|WebView|FBAN|FBAV|Instagram/i.test(ua)
            || (/iPhone|iPad/.test(ua) && !/Safari/i.test(ua))) {
            setIsWebView(true);
        }
    }, []);

    const displayError = localError || error;

    // Resend countdown timer
    useEffect(() => {
        if (resendTimer > 0) {
            timerRef.current = setTimeout(() => setResendTimer(prev => prev - 1), 1000);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [resendTimer]);

    // Clear errors on view change
    useEffect(() => {
        setLocalError(null);
        clearError();
    }, [view, clearError]);

    const navigateTo = useCallback((target: LoginView) => {
        setPassword('');
        setLocalError(null);
        clearError();
        setView(target);
    }, [clearError]);

    const handleSendMagicLink = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateEmail(email)) {
            setLocalError(t('errors.invalidEmail'));
            return;
        }
        setIsSubmitting(true);
        setLocalError(null);
        try {
            await sendMagicLink(email);
            setResendTimer(RESEND_COOLDOWN);
            setView('magic-link-sent');
        } catch {
            // Error is set by AuthProvider
        } finally {
            setIsSubmitting(false);
        }
    }, [email, sendMagicLink, t]);

    const handleResendMagicLink = useCallback(async () => {
        if (resendTimer > 0) return;
        setIsSubmitting(true);
        try {
            await sendMagicLink(email);
            setResendTimer(RESEND_COOLDOWN);
        } catch {
            // Error is set by AuthProvider
        } finally {
            setIsSubmitting(false);
        }
    }, [email, resendTimer, sendMagicLink]);

    const handlePasswordLogin = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateEmail(email)) {
            setLocalError(t('errors.invalidEmail'));
            return;
        }
        if (!password) {
            setLocalError(t('errors.passwordRequired'));
            return;
        }
        setIsSubmitting(true);
        setLocalError(null);
        try {
            await loginWithPassword(email, password);
        } catch {
            // Error is set by AuthProvider
        } finally {
            setIsSubmitting(false);
        }
    }, [email, password, loginWithPassword, t]);

    const handleForgotPassword = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateEmail(email)) {
            setLocalError(t('errors.invalidEmail'));
            return;
        }
        setIsSubmitting(true);
        setLocalError(null);
        try {
            await requestPasswordReset(email);
            setView('forgot-password-sent');
        } catch {
            // Error is set by AuthProvider
        } finally {
            setIsSubmitting(false);
        }
    }, [email, requestPasswordReset, t]);

    const renderDefaultView = () => (
        <div className={styles.viewContent}>
            <h1 className={styles.title}>{t('title')}</h1>
            <div className={styles.sectionGroup}>
                <p className={styles.sectionLabel}>{t('socialLabel')}</p>
                <div className={styles.socialRow}>
                    {!isWebView && <GoogleSignInButton className={styles.socialButton} />}
                    {!isWebView && <AmazonSignInButton className={styles.socialButton} />}
                    <TelegramSignInButton className={styles.socialButton} />
                </div>
            </div>

            <form className={styles.formSection} onSubmit={handleSendMagicLink}>
                <p className={styles.sectionText}>{t('orLoginEmail')}</p>
                <Input
                    type="email"
                    label={t('emailLabel')}
                    placeholder={t('emailPlaceholder')}
                    value={email}
                    onChange={e => { setEmail(e.target.value); setLocalError(null); }}
                    autoComplete="email"
                    disabled={isSubmitting}
                    error={displayError || undefined}
                    fullWidth
                    />

                <Button
                    type="submit"
                    variant="filled"
                    fullWidth
                    className={styles.primaryButton}
                    disabled={isSubmitting || !email}
                    loading={isSubmitting}
                    >
                    {t('sendMagicLink')}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    fullWidth
                    className={styles.ghostButton}
                    onClick={() => navigateTo('password')}
                    >
                    {t('loginWithPassword')}
                </Button>
            </form>
        </div>
    );

    const renderMagicLinkSentView = () => (
        <div className={styles.sentView}>
            <div className={styles.sentIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0f62fe" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                </svg>
            </div>
            <h2 className={styles.sentTitle}>{t('magicLinkSent.title')}</h2>
            <p className={styles.sentDescription}>
                {t('magicLinkSent.description')} <span className={styles.sentEmail}>{email}</span>
            </p>

            {displayError && <div className={styles.errorMessage}>{displayError}</div>}

            <div className={styles.resendRow}>
                {resendTimer > 0 ? (
                    <>
                        <span>{t('magicLinkSent.resendIn')}</span>
                        <span className={styles.resendTimer}>{resendTimer}s</span>
                    </>
                ) : (
                    <Button
                        type="button"
                        variant="ghost"
                        className={styles.linkButton}
                        onClick={handleResendMagicLink}
                        disabled={isSubmitting}
                    >
                        {t('magicLinkSent.resend')}
                    </Button>
                )}
            </div>

            <Button
                type="button"
                variant="ghost"
                fullWidth
                className={styles.ghostButton}
                onClick={() => navigateTo('default')}
            >
                {t('back')}
            </Button>
        </div>
    );

    const renderPasswordView = () => (
        <div className={styles.viewContent}>
            <h1 className={styles.title}>{t('passwordLogin.title')}</h1>

            <form className={styles.formSection} onSubmit={handlePasswordLogin}>
                <Input
                    type="email"
                    label={t('passwordLogin.emailLabel')}
                    placeholder={t('emailPlaceholder')}
                    value={email}
                    onChange={e => { setEmail(e.target.value); setLocalError(null); }}
                    autoComplete="email"
                    disabled={isSubmitting}
                    fullWidth
                    />

                <PasswordInput
                    label={t('passwordLogin.passwordLabel')}
                    placeholder={t('passwordLogin.passwordPlaceholder')}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setLocalError(null); }}
                    autoComplete="current-password"
                    disabled={isSubmitting}
                    fullWidth
                    />

                <div className={styles.forgotPasswordRow}>
                    <button type="button" className={styles.linkButton} onClick={() => navigateTo('forgot-password')}>
                        {t('passwordLogin.forgotPassword')}
                    </button>
                </div>

                {displayError && <div className={styles.errorMessage}>{displayError}</div>}

                <Button
                    type="submit"
                    variant="filled"
                    fullWidth
                    className={styles.primaryButton}
                    disabled={isSubmitting || !email || !password}
                    loading={isSubmitting}
                    >
                    {t('title')}
                </Button>
                <Button type="button" variant="ghost" className={styles.ghostButton} onClick={() => navigateTo('default')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    {t('back')}
                </Button>
            </form>
        </div>
    );

    const renderForgotPasswordView = () => (
        <div className={styles.viewContent}>
            <h1 className={styles.title}>{t('forgotPassword.title')}</h1>
            <p className={styles.sectionText}>{t('forgotPassword.description')}</p>

            <form className={styles.formSection} onSubmit={handleForgotPassword}>
                <Input
                    type="email"
                    label={t('passwordLogin.emailLabel')}
                    placeholder={t('emailPlaceholder')}
                    value={email}
                    onChange={e => { setEmail(e.target.value); setLocalError(null); }}
                    autoComplete="email"
                    disabled={isSubmitting}
                    error={displayError || undefined}
                    fullWidth
                    />

                <Button
                    type="submit"
                    variant="filled"
                    fullWidth
                    className={styles.primaryButton}
                    disabled={isSubmitting || !email}
                    loading={isSubmitting}
                    >
                    {t('forgotPassword.submit')}
                </Button>
                <Button type="button" variant="ghost" className={styles.ghostButton} onClick={() => navigateTo('password')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    {t('back')}
                </Button>
            </form>
        </div>
    );

    const renderForgotPasswordSentView = () => (
        <div className={styles.sentView}>
            <div className={styles.sentIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0f62fe" strokeWidth="2">
                    <path d="M9 12l2 2 4-4" />
                    <circle cx="12" cy="12" r="10" />
                </svg>
            </div>
            <h2 className={styles.sentTitle}>{t('forgotPassword.sentTitle')}</h2>
            <p className={styles.sentDescription}>
                {t('forgotPassword.sentDescription')} <span className={styles.sentEmail}>{email}</span>
            </p>

            <Button
                type="button"
                variant="outline"
                fullWidth
                // className={styles.primaryButton}
                onClick={() => navigateTo('password')}
            >
                {t('forgotPassword.backToLogin')}
            </Button>
        </div>
    );

    return (
        <PublicRouteGuard>
            <div className={styles.loginContainer} style={{ ['--login-bg' as string]: `url(${loginBg.src})` }}>
                <div className={styles.card}>
                    <div className={styles.cardTop}>
                        <Image src={logoicon} height={40} alt="Wizium" className={styles.logo} />
                        {view === 'default' && renderDefaultView()}
                        {view === 'magic-link-sent' && renderMagicLinkSentView()}
                        {view === 'password' && renderPasswordView()}
                        {view === 'forgot-password' && renderForgotPasswordView()}
                        {view === 'forgot-password-sent' && renderForgotPasswordSentView()}
                    </div>

                    <div className={styles.cardBottom}>
                        <div className={styles.infoLine}>
                            <Image src={shieldIcon} width={16} height={16} alt="" />
                            <span>{t('info.secureAuth')}</span>
                        </div>
                        <div className={styles.infoLine}>
                            <Image src={lockIcon} width={16} height={16} alt="" />
                            <span>{t('info.dataProtection')}</span>
                        </div>
                        <div className={styles.infoLine}>
                            <Image src={zipIcon} width={16} height={16} alt="" />
                            <span>{t('info.fastAccess')}</span>
                        </div>
                    </div>
                </div>

                <div className={styles.footer}>
                    <p>
                        {t.rich('terms', {
                            terms: chunks => <Link href="/legal-information/tos">{chunks}</Link>,
                            privacy: chunks => <Link href="/legal-information/privacy-policy">{chunks}</Link>,
                        })}
                    </p>
                </div>

                {isLoading && <Spinner text={t('loader')} showOverlay />}
            </div>
        </PublicRouteGuard>
    );
};

export default LoginPage;
